import React, { useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import UserNav from '../../components/navbar/user-nav';
import Breadcrumb from '../../components/Breadcrumb';

interface Miembro {
  id: string;
  nombre: string;
  nombreEnFlex?: string;
  correo: string;
  tipo: 'asociado' | 'asesor';
  inmobiliaria_doc_id?: string;
  inmobiliaria_doc_ids?: string[];
}

interface Propiedad {
  id: string;
  list_number: string;
  titulo: string;
  direccion?: string;
  precio_actual_mxn?: number;
  asesor_doc_ids?: string[];
  asociado_doc_id?: string;
  inmobiliaria_doc_id?: string;
  status?: string;
}

interface Inmobiliaria {
  id: string;
  nombre: string;
}

const ReporteAsignaciones: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useUser();

  const [loading, setLoading] = useState(false);
  const [miembrosSinProps, setMiembrosSinProps] = useState<Miembro[]>([]);
  const [propsSinMiembro, setPropsSinMiembro] = useState<Propiedad[]>([]);
  const [propsSinAgencia, setPropsSinAgencia] = useState<Propiedad[]>([]);
  const [inmobiliarias, setInmobiliarias] = useState<Map<string, string>>(new Map());
  const [resumen, setResumen] = useState<any>(null);
  const [filtroMiembros, setFiltroMiembros] = useState('');
  const [filtroProps, setFiltroProps] = useState('');

  // Diagnóstico
  const [todosLosMiembros, setTodosLosMiembros] = useState<Miembro[]>([]);
  const [todasLasProps, setTodasLasProps] = useState<Propiedad[]>([]);
  const [busquedaListNumber, setBusquedaListNumber] = useState('');
  const [busquedaMiembro, setBusquedaMiembro] = useState('');
  const [resultadoDiag, setResultadoDiag] = useState<any>(null);

  const ejecutarReporte = async () => {
    setLoading(true);

    try {
      // Cargar inmobiliarias
      const inmSnap = await getDocs(collection(db, 'inmobiliaria'));
      const inmMap = new Map<string, string>();
      inmSnap.docs.forEach(d => {
        inmMap.set(d.id, d.data().nombre || d.id);
      });
      setInmobiliarias(inmMap);

      // Cargar miembros
      const asociadosSnap = await getDocs(collection(db, 'asociado'));
      const asesoresSnap = await getDocs(collection(db, 'asesor'));

      const miembros: Miembro[] = [
        ...asociadosSnap.docs.map(d => ({
          id: d.id,
          nombre: d.data().nombre || '',
          nombreEnFlex: d.data().nombreEnFlex || '',
          correo: d.data().correo || '',
          tipo: 'asociado' as const,
          inmobiliaria_doc_id: d.data().inmobiliaria_doc_id,
          inmobiliaria_doc_ids: d.data().inmobiliaria_doc_ids
        })),
        ...asesoresSnap.docs.map(d => ({
          id: d.id,
          nombre: d.data().nombre || '',
          nombreEnFlex: d.data().nombreEnFlex || '',
          correo: d.data().correo || '',
          tipo: 'asesor' as const,
          inmobiliaria_doc_id: d.data().inmobiliaria_doc_id,
          inmobiliaria_doc_ids: d.data().inmobiliaria_doc_ids
        }))
      ];

      // Cargar propiedades
      const propSnap = await getDocs(collection(db, 'propiedades'));
      const propiedades: Propiedad[] = propSnap.docs.map(d => ({
        id: d.id,
        list_number: d.data().list_number || '',
        titulo: d.data().titulo || d.data().nombre || '',
        direccion: d.data().direccion || d.data().ubicacion || '',
        precio_actual_mxn: d.data().precio_actual_mxn || 0,
        asesor_doc_ids: d.data().asesor_doc_ids || [],
        asociado_doc_id: d.data().asociado_doc_id || '',
        inmobiliaria_doc_id: d.data().inmobiliaria_doc_id || '',
        status: d.data().status || ''
      }));

      // Contar propiedades por miembro
      const propsPorMiembro = new Map<string, number>();
      propiedades.forEach(p => {
        if (p.asesor_doc_ids && p.asesor_doc_ids.length > 0) {
          p.asesor_doc_ids.forEach(id => {
            propsPorMiembro.set(id, (propsPorMiembro.get(id) || 0) + 1);
          });
        }
        if (p.asociado_doc_id) {
          propsPorMiembro.set(p.asociado_doc_id, (propsPorMiembro.get(p.asociado_doc_id) || 0) + 1);
        }
      });

      // Miembros sin propiedades
      const sinProps = miembros.filter(m => !propsPorMiembro.has(m.id));
      setMiembrosSinProps(sinProps);

      // Guardar todos para diagnóstico
      setTodosLosMiembros(miembros);
      setTodasLasProps(propiedades);

      // Propiedades sin miembro asignado
      const sinMiembro = propiedades.filter(p =>
        (!p.asesor_doc_ids || p.asesor_doc_ids.length === 0) && !p.asociado_doc_id
      );
      setPropsSinMiembro(sinMiembro);

      // Propiedades sin agencia asignada
      const sinAgencia = propiedades.filter(p => !p.inmobiliaria_doc_id);
      setPropsSinAgencia(sinAgencia);

      // Propiedades con agencia
      const conAgencia = propiedades.filter(p => p.inmobiliaria_doc_id);

      // Resumen
      setResumen({
        totalMiembros: miembros.length,
        totalAsociados: miembros.filter(m => m.tipo === 'asociado').length,
        totalAsesores: miembros.filter(m => m.tipo === 'asesor').length,
        miembrosConProps: miembros.length - sinProps.length,
        miembrosSinProps: sinProps.length,
        totalPropiedades: propiedades.length,
        propsConMiembro: propiedades.length - sinMiembro.length,
        propsSinMiembro: sinMiembro.length,
        propsConAgencia: conAgencia.length,
        propsSinAgencia: sinAgencia.length
      });

    } catch (error: any) {
      console.error('Error:', error);
      alert('Error: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Funciones de diagnóstico
  const buscarPropiedad = () => {
    if (!busquedaListNumber.trim()) return;
    const prop = todasLasProps.find(p => p.list_number === busquedaListNumber.trim());
    if (prop) {
      const asesoresAsignados = todosLosMiembros.filter(m =>
        prop.asesor_doc_ids?.includes(m.id)
      );
      const asociadoAsignado = todosLosMiembros.find(m => m.id === prop.asociado_doc_id);
      const agenciaAsignada = prop.inmobiliaria_doc_id ? inmobiliarias.get(prop.inmobiliaria_doc_id) : null;

      setResultadoDiag({
        tipo: 'propiedad',
        propiedad: prop,
        asesores: asesoresAsignados,
        asociado: asociadoAsignado,
        agencia: agenciaAsignada
      });
    } else {
      setResultadoDiag({
        tipo: 'propiedad',
        error: `No se encontró propiedad con list_number: ${busquedaListNumber}`
      });
    }
  };

  const buscarMiembroConProps = () => {
    if (!busquedaMiembro.trim()) return;
    const busquedaLower = busquedaMiembro.toLowerCase();
    const miembro = todosLosMiembros.find(m =>
      m.nombre.toLowerCase().includes(busquedaLower) ||
      (m.nombreEnFlex || '').toLowerCase().includes(busquedaLower)
    );
    if (miembro) {
      const propsDelMiembro = todasLasProps.filter(p =>
        p.asesor_doc_ids?.includes(miembro.id) || p.asociado_doc_id === miembro.id
      );
      setResultadoDiag({
        tipo: 'miembro',
        miembro,
        propiedades: propsDelMiembro,
        agencia: miembro.inmobiliaria_doc_id ? inmobiliarias.get(miembro.inmobiliaria_doc_id) : null
      });
    } else {
      setResultadoDiag({
        tipo: 'miembro',
        error: `No se encontró miembro: ${busquedaMiembro}`
      });
    }
  };

  // Verificar permisos
  if (!user?.tipo?.includes('admin')) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger">
          <h4>Acceso denegado</h4>
          <button className="btn btn-primary" onClick={() => navigate('/admin-dashboard')}>
            Volver al Dashboard
          </button>
        </div>
      </div>
    );
  }

  const miembrosFiltrados = miembrosSinProps.filter(m =>
    !filtroMiembros ||
    m.nombre.toLowerCase().includes(filtroMiembros.toLowerCase()) ||
    (m.nombreEnFlex || '').toLowerCase().includes(filtroMiembros.toLowerCase())
  );

  const propsFiltradas = propsSinMiembro.filter(p =>
    !filtroProps ||
    p.list_number.toLowerCase().includes(filtroProps.toLowerCase()) ||
    p.titulo.toLowerCase().includes(filtroProps.toLowerCase())
  );

  return (
    <>
      <UserNav />
      <div className="container-fluid py-4">
        <Breadcrumb
          items={[
            { label: 'Dashboard', path: '/admin-dashboard' },
            { label: 'Reporte de Asignaciones' }
          ]}
        />

        <div className="card shadow-sm mb-4">
          <div className="card-header bg-info text-white d-flex justify-content-between align-items-center">
            <h4 className="mb-0">
              <i className="fas fa-chart-bar me-2"></i>
              Reporte de Asignaciones
            </h4>
            <button
              className="btn btn-light"
              onClick={ejecutarReporte}
              disabled={loading}
            >
              {loading ? (
                <><span className="spinner-border spinner-border-sm me-2"></span>Cargando...</>
              ) : (
                <><i className="fas fa-sync me-2"></i>Generar Reporte</>
              )}
            </button>
          </div>

          <div className="card-body">
            {/* Resumen */}
            {resumen && (
              <div className="row mb-4">
                <div className="col-md-4">
                  <div className="card border-primary">
                    <div className="card-header bg-primary text-white">
                      <i className="fas fa-users me-2"></i>Miembros
                    </div>
                    <div className="card-body">
                      <table aria-label="Tabla de Reporte Asignaciones" className="table table-sm mb-0">
                        <tbody>
                          <tr>
                            <td>Total miembros</td>
                            <td className="text-end"><strong>{resumen.totalMiembros}</strong></td>
                          </tr>
                          <tr>
                            <td>├ Asociados</td>
                            <td className="text-end">{resumen.totalAsociados}</td>
                          </tr>
                          <tr>
                            <td>└ Asesores</td>
                            <td className="text-end">{resumen.totalAsesores}</td>
                          </tr>
                          <tr className="table-success">
                            <td>✅ Con propiedades</td>
                            <td className="text-end"><strong>{resumen.miembrosConProps}</strong></td>
                          </tr>
                          <tr className="table-warning">
                            <td>⚠️ Sin propiedades</td>
                            <td className="text-end"><strong>{resumen.miembrosSinProps}</strong></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                <div className="col-md-4">
                  <div className="card border-success">
                    <div className="card-header bg-success text-white">
                      <i className="fas fa-home me-2"></i>Propiedades → Miembros
                    </div>
                    <div className="card-body">
                      <table aria-label="Tabla de Reporte Asignaciones" className="table table-sm mb-0">
                        <tbody>
                          <tr>
                            <td>Total propiedades</td>
                            <td className="text-end"><strong>{resumen.totalPropiedades}</strong></td>
                          </tr>
                          <tr className="table-success">
                            <td>✅ Con miembro asignado</td>
                            <td className="text-end"><strong>{resumen.propsConMiembro}</strong></td>
                          </tr>
                          <tr className="table-danger">
                            <td>❌ Sin miembro asignado</td>
                            <td className="text-end"><strong>{resumen.propsSinMiembro}</strong></td>
                          </tr>
                          <tr>
                            <td>% Asignadas</td>
                            <td className="text-end">
                              <strong>{((resumen.propsConMiembro / resumen.totalPropiedades) * 100).toFixed(1)}%</strong>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                <div className="col-md-4">
                  <div className="card border-warning">
                    <div className="card-header bg-warning text-dark">
                      <i className="fas fa-building me-2"></i>Propiedades → Agencias
                    </div>
                    <div className="card-body">
                      <table aria-label="Tabla de Reporte Asignaciones" className="table table-sm mb-0">
                        <tbody>
                          <tr>
                            <td>Total propiedades</td>
                            <td className="text-end"><strong>{resumen.totalPropiedades}</strong></td>
                          </tr>
                          <tr className="table-success">
                            <td>✅ Con agencia asignada</td>
                            <td className="text-end"><strong>{resumen.propsConAgencia}</strong></td>
                          </tr>
                          <tr className="table-danger">
                            <td>❌ Sin agencia asignada</td>
                            <td className="text-end"><strong>{resumen.propsSinAgencia}</strong></td>
                          </tr>
                          <tr>
                            <td>% Asignadas</td>
                            <td className="text-end">
                              <strong>{((resumen.propsConAgencia / resumen.totalPropiedades) * 100).toFixed(1)}%</strong>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Tabs */}
            {resumen && (
              <ul className="nav nav-tabs" id="reporteTabs" role="tablist">
                <li className="nav-item">
                  <button
                    className="nav-link active"
                    data-bs-toggle="tab"
                    data-bs-target="#miembrosSinProps"
                  >
                    <i className="fas fa-users me-1"></i>
                    Miembros sin propiedades ({miembrosSinProps.length})
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className="nav-link"
                    data-bs-toggle="tab"
                    data-bs-target="#propsSinMiembro"
                  >
                    <i className="fas fa-home me-1"></i>
                    Propiedades sin miembro ({propsSinMiembro.length})
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className="nav-link"
                    data-bs-toggle="tab"
                    data-bs-target="#propsSinAgencia"
                  >
                    <i className="fas fa-building me-1"></i>
                    Propiedades sin agencia ({propsSinAgencia.length})
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className="nav-link"
                    data-bs-toggle="tab"
                    data-bs-target="#diagnostico"
                  >
                    <i className="fas fa-search me-1"></i>
                    Diagnóstico
                  </button>
                </li>
              </ul>
            )}

            <div className="tab-content mt-3">
              {/* Tab: Miembros sin propiedades */}
              <div className="tab-pane fade show active" id="miembrosSinProps">
                {miembrosSinProps.length > 0 && (
                  <>
                    <div className="mb-3">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="🔍 Filtrar por nombre..."
                        value={filtroMiembros}
                        onChange={(e) => setFiltroMiembros(e.target.value)}
                      />
                    </div>
                    <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                      <table aria-label="Tabla de Reporte Asignaciones" className="table table-sm table-striped table-hover">
                        <thead className="sticky-top bg-white">
                          <tr>
                            <th scope="col">#</th>
                            <th scope="col">Nombre</th>
                            <th scope="col">Nombre en Flex</th>
                            <th scope="col">Tipo</th>
                            <th scope="col">Inmobiliaria</th>
                            <th scope="col">Correo</th>
                          </tr>
                        </thead>
                        <tbody>
                          {miembrosFiltrados.map((m, idx) => (
                            <tr key={m.id}>
                              <td>{idx + 1}</td>
                              <td><strong>{m.nombre}</strong></td>
                              <td><small className="text-muted">{m.nombreEnFlex || '-'}</small></td>
                              <td>
                                <span className={`badge ${m.tipo === 'asociado' ? 'bg-success' : 'bg-info'}`}>
                                  {m.tipo}
                                </span>
                              </td>
                              <td>
                                <small>
                                  {m.inmobiliaria_doc_id ? inmobiliarias.get(m.inmobiliaria_doc_id) || m.inmobiliaria_doc_id : '-'}
                                </small>
                              </td>
                              <td><small>{m.correo}</small></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>

              {/* Tab: Propiedades sin miembro */}
              <div className="tab-pane fade" id="propsSinMiembro">
                {propsSinMiembro.length > 0 && (
                  <>
                    <div className="mb-3">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="🔍 Filtrar por list number o título..."
                        value={filtroProps}
                        onChange={(e) => setFiltroProps(e.target.value)}
                      />
                    </div>
                    <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                      <table aria-label="Tabla de Reporte Asignaciones" className="table table-sm table-striped table-hover">
                        <thead className="sticky-top bg-white">
                          <tr>
                            <th scope="col">#</th>
                            <th scope="col">List Number</th>
                            <th scope="col">Título</th>
                            <th scope="col">Agencia</th>
                            <th scope="col">Precio MXN</th>
                          </tr>
                        </thead>
                        <tbody>
                          {propsFiltradas.slice(0, 200).map((p, idx) => (
                            <tr key={p.id}>
                              <td>{idx + 1}</td>
                              <td><strong>{p.list_number}</strong></td>
                              <td><small>{p.titulo?.substring(0, 50)}...</small></td>
                              <td>
                                {p.inmobiliaria_doc_id ? (
                                  <span className="badge bg-success">
                                    {inmobiliarias.get(p.inmobiliaria_doc_id) || p.inmobiliaria_doc_id}
                                  </span>
                                ) : (
                                  <span className="badge bg-danger">Sin agencia</span>
                                )}
                              </td>
                              <td>${p.precio_actual_mxn?.toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {propsFiltradas.length > 200 && (
                        <p className="text-muted">Mostrando 200 de {propsFiltradas.length}</p>
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* Tab: Propiedades sin agencia */}
              <div className="tab-pane fade" id="propsSinAgencia">
                {propsSinAgencia.length > 0 ? (
                  <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    <table aria-label="Tabla de Reporte Asignaciones" className="table table-sm table-striped table-hover">
                      <thead className="sticky-top bg-white">
                        <tr>
                          <th scope="col">#</th>
                          <th scope="col">List Number</th>
                          <th scope="col">Título</th>
                          <th scope="col">Tiene Miembro?</th>
                          <th scope="col">Precio MXN</th>
                        </tr>
                      </thead>
                      <tbody>
                        {propsSinAgencia.slice(0, 200).map((p, idx) => (
                          <tr key={p.id}>
                            <td>{idx + 1}</td>
                            <td><strong>{p.list_number}</strong></td>
                            <td><small>{p.titulo?.substring(0, 50)}...</small></td>
                            <td>
                              {(p.asesor_doc_ids && p.asesor_doc_ids.length > 0) || p.asociado_doc_id ? (
                                <span className="badge bg-success">Sí</span>
                              ) : (
                                <span className="badge bg-danger">No</span>
                              )}
                            </td>
                            <td>${p.precio_actual_mxn?.toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {propsSinAgencia.length > 200 && (
                      <p className="text-muted">Mostrando 200 de {propsSinAgencia.length}</p>
                    )}
                  </div>
                ) : resumen && (
                  <div className="alert alert-success">
                    <i className="fas fa-check-circle me-2"></i>
                    ¡Todas las propiedades tienen agencia asignada!
                  </div>
                )}
              </div>

              {/* Tab: Diagnóstico */}
              <div className="tab-pane fade" id="diagnostico">
                <div className="row">
                  <div className="col-md-6">
                    <div className="card border-info mb-3">
                      <div className="card-header bg-info text-white">
                        <i className="fas fa-home me-2"></i>Buscar Propiedad por List Number
                      </div>
                      <div className="card-body">
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Ej: 35625, 37662, 38248..."
                            value={busquedaListNumber}
                            onChange={(e) => setBusquedaListNumber(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && buscarPropiedad()}
                          />
                          <button className="btn btn-info" onClick={buscarPropiedad}>
                            <i className="fas fa-search"></i>
                          </button>
                        </div>
                        <small className="text-muted">
                          Propiedades de Christianne: 35625, 37662, 38248<br/>
                          Propiedades de Ruth Carolina: 39044
                        </small>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="card border-success mb-3">
                      <div className="card-header bg-success text-white">
                        <i className="fas fa-user me-2"></i>Buscar Miembro y sus Propiedades
                      </div>
                      <div className="card-body">
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Nombre del miembro..."
                            value={busquedaMiembro}
                            onChange={(e) => setBusquedaMiembro(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && buscarMiembroConProps()}
                          />
                          <button className="btn btn-success" onClick={buscarMiembroConProps}>
                            <i className="fas fa-search"></i>
                          </button>
                        </div>
                        <small className="text-muted">
                          Busca por nombre o nombreEnFlex
                        </small>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Resultados del diagnóstico */}
                {resultadoDiag && (
                  <div className="card mt-3">
                    <div className="card-header bg-dark text-white">
                      <i className="fas fa-clipboard-list me-2"></i>Resultado del Diagnóstico
                    </div>
                    <div className="card-body">
                      {resultadoDiag.error ? (
                        <div className="alert alert-danger mb-0">{resultadoDiag.error}</div>
                      ) : resultadoDiag.tipo === 'propiedad' ? (
                        <div>
                          <h6>Propiedad: {resultadoDiag.propiedad.list_number}</h6>
                          <table aria-label="Tabla de Reporte Asignaciones" className="table table-sm">
                            <tbody>
                              <tr>
                                <td><strong>Título:</strong></td>
                                <td>{resultadoDiag.propiedad.titulo}</td>
                              </tr>
                              <tr>
                                <td><strong>Doc ID:</strong></td>
                                <td><code>{resultadoDiag.propiedad.id}</code></td>
                              </tr>
                              <tr>
                                <td><strong>Agencia asignada:</strong></td>
                                <td>
                                  {resultadoDiag.agencia ? (
                                    <span className="badge bg-success">{resultadoDiag.agencia}</span>
                                  ) : (
                                    <span className="badge bg-danger">Sin agencia</span>
                                  )}
                                  <small className="text-muted ms-2">
                                    ({resultadoDiag.propiedad.inmobiliaria_doc_id || 'vacío'})
                                  </small>
                                </td>
                              </tr>
                              <tr>
                                <td><strong>asesor_doc_ids:</strong></td>
                                <td>
                                  {resultadoDiag.propiedad.asesor_doc_ids?.length > 0 ? (
                                    <span className="badge bg-success">
                                      {resultadoDiag.propiedad.asesor_doc_ids.length} asesores
                                    </span>
                                  ) : (
                                    <span className="badge bg-warning text-dark">Vacío</span>
                                  )}
                                  <br/>
                                  <small><code>{JSON.stringify(resultadoDiag.propiedad.asesor_doc_ids)}</code></small>
                                </td>
                              </tr>
                              <tr>
                                <td><strong>asociado_doc_id:</strong></td>
                                <td>
                                  {resultadoDiag.propiedad.asociado_doc_id ? (
                                    <span className="badge bg-success">Asignado</span>
                                  ) : (
                                    <span className="badge bg-warning text-dark">Vacío</span>
                                  )}
                                  <br/>
                                  <small><code>{resultadoDiag.propiedad.asociado_doc_id || '""'}</code></small>
                                </td>
                              </tr>
                              <tr>
                                <td><strong>Asesores encontrados:</strong></td>
                                <td>
                                  {resultadoDiag.asesores?.length > 0 ? (
                                    resultadoDiag.asesores.map((a: Miembro) => (
                                      <span key={a.id} className="badge bg-info me-1">{a.nombre}</span>
                                    ))
                                  ) : (
                                    <span className="text-muted">Ninguno</span>
                                  )}
                                </td>
                              </tr>
                              <tr>
                                <td><strong>Asociado encontrado:</strong></td>
                                <td>
                                  {resultadoDiag.asociado ? (
                                    <span className="badge bg-success">{resultadoDiag.asociado.nombre}</span>
                                  ) : (
                                    <span className="text-muted">Ninguno</span>
                                  )}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div>
                          <h6>Miembro: {resultadoDiag.miembro.nombre}</h6>
                          <table aria-label="Tabla de Reporte Asignaciones" className="table table-sm">
                            <tbody>
                              <tr>
                                <td><strong>Doc ID:</strong></td>
                                <td><code>{resultadoDiag.miembro.id}</code></td>
                              </tr>
                              <tr>
                                <td><strong>Tipo:</strong></td>
                                <td>
                                  <span className={`badge ${resultadoDiag.miembro.tipo === 'asociado' ? 'bg-success' : 'bg-info'}`}>
                                    {resultadoDiag.miembro.tipo}
                                  </span>
                                </td>
                              </tr>
                              <tr>
                                <td><strong>Nombre en Flex:</strong></td>
                                <td><code>{resultadoDiag.miembro.nombreEnFlex || 'No definido'}</code></td>
                              </tr>
                              <tr>
                                <td><strong>Agencia:</strong></td>
                                <td>{resultadoDiag.agencia || 'Sin agencia'}</td>
                              </tr>
                              <tr>
                                <td><strong>Propiedades asignadas:</strong></td>
                                <td>
                                  <span className={`badge ${resultadoDiag.propiedades.length > 0 ? 'bg-success' : 'bg-danger'}`}>
                                    {resultadoDiag.propiedades.length} propiedades
                                  </span>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                          {resultadoDiag.propiedades.length > 0 && (
                            <div className="mt-2">
                              <strong>Lista de propiedades:</strong>
                              <ul className="list-group list-group-flush">
                                {resultadoDiag.propiedades.map((p: Propiedad) => (
                                  <li key={p.id} className="list-group-item d-flex justify-content-between">
                                    <span><strong>{p.list_number}</strong> - {p.titulo?.substring(0, 40)}...</span>
                                    <span>${p.precio_actual_mxn?.toLocaleString()}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ReporteAsignaciones;
